/**
 * SiteGlue Solutions — universal business outcome categories and landing pages.
 * Used by /solutions/[slug] routes and the Solutions mega menu.
 */

export type SiteGlueSolutionCategoryId =
  | "revenue-growth"
  | "marketing-growth"
  | "automation-outcomes"
  | "customer-experience"
  | "operational-efficiency";

export type SiteGlueSolutionCategory = {
  id: SiteGlueSolutionCategoryId;
  label: string;
  description: string;
};

export type SiteGlueSolutionOutcome = {
  slug: string;
  title: string;
  categoryId: SiteGlueSolutionCategoryId;
  navDescription: string;
  heroDescription: string;
  challengeLead: string;
};

export const SITEGLUE_SOLUTION_CATEGORIES: SiteGlueSolutionCategory[] = [
  {
    id: "revenue-growth",
    label: "Revenue Growth",
    description: "Turn conversations into revenue with SiteGlue Agents that capture and convert demand.",
  },
  {
    id: "marketing-growth",
    label: "Marketing Growth",
    description: "Generate leads, improve visibility, and convert more website visitors.",
  },
  {
    id: "automation-outcomes",
    label: "Automation Outcomes",
    description: "Automate repetitive work while keeping humans in control of exceptions.",
  },
  {
    id: "customer-experience",
    label: "Customer Experience",
    description: "Deliver faster, more personal support across every channel.",
  },
  {
    id: "operational-efficiency",
    label: "Operational Efficiency",
    description: "Scale operations and productivity without adding headcount.",
  },
];

export const SITEGLUE_SOLUTION_OUTCOMES: SiteGlueSolutionOutcome[] = [
  // Revenue Growth
  {
    slug: "increase-sales",
    title: "Increase Sales",
    categoryId: "revenue-growth",
    navDescription: "Capture and convert more sales conversations.",
    heroDescription:
      "SiteGlue Agents answer inbound sales questions, qualify interest, and keep follow-ups moving — so your team closes more deals without missing after-hours demand.",
    challengeLead:
      "Slow follow-up and missed inquiries cost revenue every day. SiteGlue Agents handle routine sales conversations while your team focuses on closing.",
  },
  {
    slug: "increase-appointments",
    title: "Increase Appointments",
    categoryId: "revenue-growth",
    navDescription: "Book more appointments and consultation requests.",
    heroDescription:
      "SiteGlue Agents help prospects find times, confirm details, and complete booking workflows — connected to your calendar when integrated, or capturing requests for your team.",
    challengeLead:
      "Empty calendar slots often start with unanswered calls and abandoned chats. Automate scheduling intake without losing the human touch for complex cases.",
  },
  {
    slug: "increase-donations",
    title: "Increase Donations",
    categoryId: "revenue-growth",
    navDescription: "Engage donors and simplify giving conversations.",
    heroDescription:
      "Nonprofits and churches use SiteGlue Agents to answer giving questions, guide donors, and capture interest around campaigns — with pastoral or stewardship handoffs when needed.",
    challengeLead:
      "Donor interest spikes during campaigns but staff cannot be online 24/7. Extend reach while keeping meaningful human relationships.",
  },
  {
    slug: "increase-memberships",
    title: "Increase Memberships",
    categoryId: "revenue-growth",
    navDescription: "Grow memberships with always-on intake.",
    heroDescription:
      "Answer membership questions, explain benefits, and capture sign-up intent across phone, chat, and SMS — routing high-intent prospects to your team with context.",
    challengeLead:
      "Prospective members research on their schedule. SiteGlue Agents keep conversations warm until your team can close.",
  },
  {
    slug: "increase-enrollments",
    title: "Increase Enrollments",
    categoryId: "revenue-growth",
    navDescription: "Convert more admissions and enrollment inquiries.",
    heroDescription:
      "Schools and programs use SiteGlue Agents to guide families through admissions questions, tour requests, and enrollment steps — with counselors stepping in when guidance matters.",
    challengeLead:
      "Families compare options quickly. Faster, consistent answers improve enrollment conversion without burning out admissions staff.",
  },

  // Marketing Growth
  {
    slug: "generate-more-leads",
    title: "Generate More Leads",
    categoryId: "marketing-growth",
    navDescription: "Capture qualified leads from every channel.",
    heroDescription:
      "SiteGlue Agents engage website visitors, answer campaign questions, and collect structured lead data — so marketing spend converts into conversations your team can act on.",
    challengeLead:
      "Traffic without capture is wasted budget. Turn visits and calls into qualified leads automatically.",
  },
  {
    slug: "increase-website-conversions",
    title: "Increase Website Conversions",
    categoryId: "marketing-growth",
    navDescription: "Turn more visitors into conversations and customers.",
    heroDescription:
      "Proactive chat, instant answers, and clear next steps help visitors take action — book, buy, or request contact — instead of bouncing.",
    challengeLead:
      "Most visitors leave without engaging. SiteGlue Agents reduce friction at the moment of intent.",
  },
  {
    slug: "improve-seo-and-local-rankings",
    title: "Improve SEO and Local Rankings",
    categoryId: "marketing-growth",
    navDescription: "Support local discovery with better engagement signals.",
    heroDescription:
      "Fast responses, consistent NAP-aligned answers, and helpful local content in conversations support the engagement and trust signals that complement SEO — without replacing your marketing strategy.",
    challengeLead:
      "Local search rewards businesses that respond. SiteGlue Agents help you never miss a local inquiry while your team focuses on content and campaigns.",
  },
  {
    slug: "increase-online-reviews",
    title: "Increase Online Reviews",
    categoryId: "marketing-growth",
    navDescription: "Improve satisfaction moments that lead to reviews.",
    heroDescription:
      "Great experiences start with fast, helpful conversations. SiteGlue Agents resolve routine issues quickly and route unhappy customers to humans before problems become public reviews.",
    challengeLead:
      "Reviews reflect real interactions. Better response times and follow-through create more promoters.",
  },
  {
    slug: "improve-customer-engagement",
    title: "Improve Customer Engagement",
    categoryId: "marketing-growth",
    navDescription: "Stay connected across channels customers prefer.",
    heroDescription:
      "Meet customers on phone, chat, SMS, email, and social — with one SiteGlue Agent workflow that keeps engagement consistent and measurable.",
    challengeLead:
      "Siloed channels create dropped threads. Unified AI coverage keeps relationships active.",
  },

  // Automation Outcomes
  {
    slug: "automate-repetitive-tasks",
    title: "Automate Repetitive Tasks",
    categoryId: "automation-outcomes",
    navDescription: "Offload routine work to SiteGlue Agents.",
    heroDescription:
      "FAQ answers, intake forms, scheduling requests, and status questions consume hours daily. SiteGlue Agents handle the repeatable work under rules your team approves.",
    challengeLead:
      "Skilled staff should not spend days on copy-paste work. Automate the routine, escalate the rest.",
  },
  {
    slug: "reduce-administrative-costs",
    title: "Reduce Administrative Costs",
    categoryId: "automation-outcomes",
    navDescription: "Lower overhead on front-office workload.",
    heroDescription:
      "Cover more conversations without adding headcount. SiteGlue Agents scale administrative communication for a predictable software cost instead of overtime and missed opportunities.",
    challengeLead:
      "Administrative drag shows up in payroll and lost revenue. AI augments your existing team first.",
  },
  {
    slug: "improve-response-times",
    title: "Improve Response Times",
    categoryId: "automation-outcomes",
    navDescription: "Answer in seconds, not hours.",
    heroDescription:
      "Instant responses on phone, chat, and SMS set expectations and keep customers moving — with Team Handoff™ when speed alone is not enough.",
    challengeLead:
      "Delayed replies lose trust. SiteGlue Agents provide immediate acknowledgment and resolution for routine requests.",
  },
  {
    slug: "automate-follow-up-communications",
    title: "Automate Follow-Up Communications",
    categoryId: "automation-outcomes",
    navDescription: "Consistent follow-ups without manual chasing.",
    heroDescription:
      "Confirmations, reminders, and nurture messages run on approved workflows so nothing falls through the cracks after the first conversation.",
    challengeLead:
      "Manual follow-up fails when teams get busy. Automate the sequence; humans handle exceptions.",
  },
  {
    slug: "streamline-workflows",
    title: "Streamline Workflows",
    categoryId: "automation-outcomes",
    navDescription: "Connect intake, scheduling, and handoffs.",
    heroDescription:
      "SiteGlue Agents guide conversations through defined steps — capture, qualify, schedule, notify — reducing handoffs and rework across your stack.",
    challengeLead:
      "Fragmented processes create duplicate data entry. Structured AI workflows keep teams aligned.",
  },

  // Customer Experience
  {
    slug: "provide-24-7-support",
    title: "Provide 24/7 Support",
    categoryId: "customer-experience",
    navDescription: "Always-on coverage across channels.",
    heroDescription:
      "Customers expect answers nights and weekends. SiteGlue Agents provide continuous coverage with Human-in-the-Loop™ escalation to your team when needed.",
    challengeLead:
      "After-hours demand is real revenue. Stop sending callers to voicemail.",
  },
  {
    slug: "improve-satisfaction",
    title: "Improve Satisfaction",
    categoryId: "customer-experience",
    navDescription: "Better experiences through faster, clearer help.",
    heroDescription:
      "Consistent, knowledgeable responses and smooth handoffs to humans when issues are complex improve satisfaction scores and retention.",
    challengeLead:
      "Frustration grows in hold queues and email delays. SiteGlue Agents remove friction from routine interactions.",
  },
  {
    slug: "personalize-customer-interactions",
    title: "Personalize Customer Interactions",
    categoryId: "customer-experience",
    navDescription: "Context-aware conversations at scale.",
    heroDescription:
      "SiteGlue Agents use your approved knowledge, location data, and conversation history to tailor responses — without exposing sensitive data or bypassing human judgment.",
    challengeLead:
      "Generic scripts feel robotic. Trained SiteGlue Agents reflect your brand voice and customer context.",
  },
  {
    slug: "reduce-wait-times",
    title: "Reduce Wait Times",
    categoryId: "customer-experience",
    navDescription: "Shorter queues on phone and chat.",
    heroDescription:
      "Handle tier-one questions instantly so live staff focus on cases that need them — cutting average wait without sacrificing quality.",
    challengeLead:
      "Long waits drive churn. AI absorbs volume so humans handle fewer, higher-value queues.",
  },
  {
    slug: "improve-communication",
    title: "Improve Communication",
    categoryId: "customer-experience",
    navDescription: "Clear, consistent messaging everywhere.",
    heroDescription:
      "One SiteGlue Agent platform for phone, chat, SMS, email, and more — so customers get the same accurate answers regardless of channel.",
    challengeLead:
      "Inconsistent answers erode trust. Centralized AI communication keeps messaging aligned.",
  },

  // Operational Efficiency
  {
    slug: "scale-without-hiring",
    title: "Scale Without Hiring",
    categoryId: "operational-efficiency",
    navDescription: "Grow conversation capacity on software.",
    heroDescription:
      "Add SiteGlue Agents instead of headcount for routine communication — scale seasonality and campaigns without a hiring cycle.",
    challengeLead:
      "Growth should not always mean more desks. SiteGlue Agents expand capacity immediately.",
  },
  {
    slug: "improve-employee-productivity",
    title: "Improve Employee Productivity",
    categoryId: "operational-efficiency",
    navDescription: "Free staff for high-value work.",
    heroDescription:
      "When AI handles intake, FAQs, and scheduling, your team spends time on relationships, judgment calls, and revenue-generating activities.",
    challengeLead:
      "Interruptions destroy focus. SiteGlue Agents filter and resolve routine demand first.",
  },
  {
    slug: "reduce-manual-work",
    title: "Reduce Manual Work",
    categoryId: "operational-efficiency",
    navDescription: "Less data entry and phone tag.",
    heroDescription:
      "Structured capture from conversations feeds your workflows — fewer sticky notes, fewer missed fields, fewer callbacks to re-ask the same questions.",
    challengeLead:
      "Manual re-entry creates errors and delays. SiteGlue Agents collect once, route correctly.",
  },
  {
    slug: "improve-reporting-and-analytics",
    title: "Improve Reporting and Analytics",
    categoryId: "operational-efficiency",
    navDescription: "Conversation insights for better decisions.",
    heroDescription:
      "SiteGlue tracks conversation volume, handoff rates, knowledge gaps, and outcomes — so leaders see where AI helps and where humans should invest.",
    challengeLead:
      "You cannot improve what you do not measure. AI conversation data reveals bottlenecks and opportunities.",
  },
  {
    slug: "optimize-internal-processes",
    title: "Optimize Internal Processes",
    categoryId: "operational-efficiency",
    navDescription: "Tighter handoffs between AI and team.",
    heroDescription:
      "Define approval rules, routing, and escalation paths once — SiteGlue Agents execute consistently while Trish recommends process improvements with Human Approval.",
    challengeLead:
      "Ad-hoc processes break at scale. Standardized AI workflows create repeatable operations.",
  },
];

export const SITEGLUE_SOLUTIONS_NAV_LABEL = "Solutions";
export const SITEGLUE_SOLUTIONS_MEGA_TITLE = "Business Outcomes";
export const SITEGLUE_SOLUTIONS_MEGA_LINE =
  "Set up an AI agent for the outcome you need — always with a Human-in-the-Loop™.";

/** Product pages that already exist. Outcome URLs under /solutions stay in the menu below. */
export const SITEGLUE_SOLUTION_PRODUCT_LINKS = [
  {
    href: "/ai-receptionist",
    title: "AI Receptionist",
    description: "Answer calls and route them to the right person.",
  },
  {
    href: "/ai-phone-system",
    title: "AI Answering Service",
    description: "Answer the phone, take a message, or connect a teammate.",
  },
  {
    href: "/scheduling-specialist",
    title: "AI Appointment Scheduling",
    description: "Schedule when a calendar is connected, or capture the request.",
  },
  {
    href: "/online-support-agent",
    title: "AI Customer Support",
    description: "Answer questions and connect people with your team.",
  },
] as const;
export const SITEGLUE_SOLUTIONS_SEE_ALL_HREF = "/how-it-works";
export const SITEGLUE_SOLUTIONS_BASE_PATH = "/solutions";

export function solutionHref(slug: string): string {
  return `${SITEGLUE_SOLUTIONS_BASE_PATH}/${slug}`;
}

export function getSiteGlueSolutionBySlug(slug: string): SiteGlueSolutionOutcome | undefined {
  return SITEGLUE_SOLUTION_OUTCOMES.find((o) => o.slug === slug);
}

export function getSiteGlueSolutionsByCategory(
  categoryId: SiteGlueSolutionCategoryId,
): SiteGlueSolutionOutcome[] {
  return SITEGLUE_SOLUTION_OUTCOMES.filter((o) => o.categoryId === categoryId);
}

export function getAllSiteGlueSolutionSlugs(): string[] {
  return SITEGLUE_SOLUTION_OUTCOMES.map((o) => o.slug);
}
